/**
 * Categories API functions.
 *
 * One `category` table holds two taxonomies: note categories and the finance
 * spending categories. Every read names its `domain`; the server defaults to
 * notes. The notes filed under a category are read through the notes list
 * (`getNotes({ category })`), not here.
 */

import { apiClient, buildQueryString, PaginatedResponse } from "@/shared/lib/api/client";
import type {
  Category,
  CategoryCreateInput,
  CategoryDomain,
  CategoryListItem,
  CategoryTreeNode,
  CategoryUpdateInput,
  CategoryWithHierarchy,
} from "../types";

const BASE_PATH = "/api/categories";

export async function getCategories(params?: {
  skip?: number;
  limit?: number;
  search?: string;
  domain?: CategoryDomain;
}): Promise<PaginatedResponse<CategoryListItem>> {
  const query = buildQueryString({
    skip: params?.skip ?? 0,
    limit: params?.limit ?? 100,
    search: params?.search,
    domain: params?.domain,
  });
  return apiClient<PaginatedResponse<CategoryListItem>>(`${BASE_PATH}${query}`);
}

export async function getRootCategories(domain?: CategoryDomain): Promise<CategoryListItem[]> {
  return apiClient<CategoryListItem[]>(`${BASE_PATH}/roots${buildQueryString({ domain })}`);
}

export async function getCategoryTree(domain?: CategoryDomain): Promise<CategoryTreeNode[]> {
  return apiClient<CategoryTreeNode[]>(`${BASE_PATH}/tree${buildQueryString({ domain })}`);
}

export async function getCategory(categoryId: string): Promise<Category> {
  return apiClient<Category>(`${BASE_PATH}/${categoryId}`);
}

export async function getCategoryBySlug(slug: string): Promise<Category> {
  return apiClient<Category>(`${BASE_PATH}/slug/${slug}`);
}

export async function getCategoryWithHierarchy(
  categoryId: string
): Promise<CategoryWithHierarchy> {
  return apiClient<CategoryWithHierarchy>(`${BASE_PATH}/${categoryId}/hierarchy`);
}

export async function createCategory(data: CategoryCreateInput): Promise<Category> {
  return apiClient<Category>(BASE_PATH, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateCategory(
  categoryId: string,
  data: CategoryUpdateInput
): Promise<Category> {
  return apiClient<Category>(`${BASE_PATH}/${categoryId}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteCategory(categoryId: string): Promise<void> {
  return apiClient<void>(`${BASE_PATH}/${categoryId}`, {
    method: "DELETE",
  });
}
